import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import saveAs from 'file-saver';
import { of } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';
import * as fromActions from '../actions/annual-report-templates.actions';
import { IAnnualReportTemplateDTO } from '../interfaces/annual-report-template-dto.interface';
import { IARTemplateFilterCriteria } from '../interfaces/annual-report-template-filter-criteria.interface';
import { AumicoAnnualReportTemplatesService } from '../services/annual-report-template.service';

@Injectable()
export class AnnaulReportTemplatesEffects {
    constructor(private readonly actions$: Actions, private readonly service: AumicoAnnualReportTemplatesService) {}

    createArTemplate$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.annualReportTemplateActions.createArTemplate.type),
            mergeMap((action: fromActions.ActionWithPayload<IAnnualReportTemplateDTO>) => {
                return this.service.createArTemplate$(action.payload).pipe(
                    map((response) => {
                        return fromActions.annualReportTemplateActions.createArTemplateSuccess({
                            payload: response,
                        });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(
                            fromActions.annualReportTemplateActions.createArTemplateFailed({
                                payload: error,
                            }),
                        );
                    }),
                );
            }),
        ),
    );

    updateArTemplate$ = createEffect(() =>
        this.actions$.pipe(
            ofType(
                fromActions.annualReportTemplateActions.updateArTemplate.type,
                fromActions.annualReportTemplateActions.setArTemplateSpecifications.type,
                fromActions.annualReportTemplateActions.setArBalanceSheetTaxonomy.type,
                fromActions.annualReportTemplateActions.setArIncomeStatementTaxonomy.type,
                fromActions.annualReportTemplateActions.setArNotesTemplate.type,
                fromActions.annualReportTemplateActions.setPdfInformation.type,
                fromActions.annualReportTemplateActions.setArTemplateSettings.type,
            ),
            mergeMap((action: fromActions.ActionWithPayload<IAnnualReportTemplateDTO>) => {
                return this.service.updateArTemplate$(action.payload.id, action.payload).pipe(
                    map((response) => {
                        return fromActions.annualReportTemplateActions.updateArTemplateSuccess({
                            payload: response,
                        });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(
                            fromActions.annualReportTemplateActions.updateArTemplateFailed({
                                payload: error,
                            }),
                        );
                    }),
                );
            }),
        ),
    );

    downloadDefaultPdfTemplate = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.annualReportTemplateActions.downloadDefaultPdfTemplate.type),
            mergeMap(() => {
                return this.service.downloadDefaultPdfTemplate$().pipe(
                    map((response) => {
                        const blob = new Blob([response], {
                            type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                        });
                        saveAs(blob, 'Template.docx');
                        return fromActions.annualReportTemplateActions.downloadDefaultPdfTemplateSuccess({
                            payload: response,
                        });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(
                            fromActions.annualReportTemplateActions.downloadDefaultPdfTemplateFailed({
                                payload: error,
                            }),
                        );
                    }),
                );
            }),
        ),
    );

    downloadDefaultHgbPdfTemplate = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.annualReportTemplateActions.downloadDefaultHgbPdfTemplate.type),
            mergeMap(() => {
                return this.service.downloadDefaultHgbPdfTemplate$().pipe(
                    map((response) => {
                        const blob = new Blob([response], {
                            type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                        });
                        saveAs(blob, 'Template.docx');
                        return fromActions.annualReportTemplateActions.downloadDefaultHgbPdfTemplateSuccess({
                            payload: response,
                        });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(
                            fromActions.annualReportTemplateActions.downloadDefaultHgbPdfTemplateFailed({
                                payload: error,
                            }),
                        );
                    }),
                );
            }),
        ),
    );

    downloadPdfTemplate = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.annualReportTemplateActions.downloadPdfTemplate.type),
            mergeMap((action: fromActions.ActionWithPayload<string>) => {
                return this.service.downloadPdfTemplate$(action.payload).pipe(
                    map((response) => {
                        const blob = new Blob([response], {
                            type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                        });
                        saveAs(blob, 'Template.docx');
                        return fromActions.annualReportTemplateActions.downloadPdfTemplateSuccess({
                            payload: response,
                        });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(
                            fromActions.annualReportTemplateActions.downloadPdfTemplateFailed({
                                payload: error,
                            }),
                        );
                    }),
                );
            }),
        ),
    );

    uploadPdfTemplate = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.annualReportTemplateActions.uploadPdfTemplate.type),
            mergeMap((action: fromActions.ActionWithPayload<string>) => {
                return this.service.uploadPdfTemplate$(action.payload).pipe(
                    map((response) => {
                        return fromActions.annualReportTemplateActions.uploadPdfTemplateSuccess({
                            payload: response,
                        });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(
                            fromActions.annualReportTemplateActions.uploadPdfTemplateFailed({
                                payload: error,
                            }),
                        );
                    }),
                );
            }),
        ),
    );

    deleteArTemplate$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.annualReportTemplateActions.deleteArTemplate.type),
            mergeMap((action: fromActions.ActionWithPayload<string>) => {
                return this.service.deleteArTemplate$(action.payload).pipe(
                    map(() => {
                        return fromActions.annualReportTemplateActions.deleteArTemplateSuccess({
                            payload: action.payload,
                        });
                    }),
                    catchError((error) => {
                        return of(
                            fromActions.annualReportTemplateActions.deleteArTemplateFailed({
                                payload: error,
                            }),
                        );
                    }),
                );
            }),
        ),
    );

    getAllArTemplates$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.annualReportTemplateActions.getAllArTemplates.type),
            mergeMap(() => {
                return this.service.getAllArTemplates$().pipe(
                    map((response) => {
                        return fromActions.annualReportTemplateActions.getAllArTemplatesSuccess({
                            payload: response,
                        });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(
                            fromActions.annualReportTemplateActions.getAllArTemplatesFailed({
                                payload: error,
                            }),
                        );
                    }),
                );
            }),
        ),
    );

    getArTemplateById$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.annualReportTemplateActions.getArTemplateById.type),
            mergeMap((action: fromActions.ActionWithPayload<string>) => {
                return this.service.getArTemplateById$(action.payload).pipe(
                    map((response) => {
                        return fromActions.annualReportTemplateActions.getArTemplateByIdSuccess({
                            payload: response,
                        });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(
                            fromActions.annualReportTemplateActions.getArTemplateByIdFailed({
                                payload: error,
                            }),
                        );
                    }),
                );
            }),
        ),
    );

    getArTemplatesByFilter$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.annualReportTemplateActions.getArTemplatesByFilter.type),
            mergeMap((action: fromActions.ActionWithPayload<IARTemplateFilterCriteria>) => {
                return this.service.getArTemplatesByFilter$(action.payload).pipe(
                    map((response) => {
                        return fromActions.annualReportTemplateActions.getArTemplatesByFilterSuccess({
                            payload: response,
                        });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(
                            fromActions.annualReportTemplateActions.getArTemplatesByFilterFailed({
                                payload: error,
                            }),
                        );
                    }),
                );
            }),
        ),
    );

    getArTemplateLocales$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.annualReportTemplateActions.getArTemplateLocales.type),
            mergeMap((action: fromActions.ActionWithPayload<string>) => {
                return this.service.getArTemplateLocales$(action.payload).pipe(
                    map((response) => {
                        return fromActions.annualReportTemplateActions.getArTemplateLocalesSuccess({
                            payload: response,
                        });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(
                            fromActions.annualReportTemplateActions.getArTemplateLocalesFailed({
                                payload: error,
                            }),
                        );
                    }),
                );
            }),
        ),
    );

    getAnnualReportTemplateTranslations$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.arTemplateTranslationsActions.getTranslations),
            mergeMap((action) => {
                return this.service.getTranslations(action.locales, action.untranslatedOnly).pipe(
                    map((response) => {
                        return fromActions.arTemplateTranslationsActions.onGetTranslationsSuccess({
                            payload: response,
                        });
                    }),
                    catchError((error) => {
                        return of(
                            fromActions.arTemplateTranslationsActions.onGetTranslationsFailed({ payload: error }),
                        );
                    }),
                );
            }),
        ),
    );

    updateAnnualReportTemplateTranslations$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.arTemplateTranslationsActions.updateTranslations),
            mergeMap((action) => {
                const observable$ = this.service.updateTranslations(action.payload);
                return observable$.pipe(
                    map(() => {
                        return fromActions.arTemplateTranslationsActions.onUpdateTranslationsSuccess();
                    }),
                    catchError((error) => {
                        return of(
                            fromActions.arTemplateTranslationsActions.onUpdateTranslationsFailed({
                                payload: error,
                            }),
                        );
                    }),
                );
            }),
        ),
    );
}
